import React, { useState, useEffect } from 'react';
import { llmConfigService } from '../../services';
import { llmModelService } from '../../services/llmModelService';
import { LLMConfig, CreateLLMConfigRequest, Domain, ReasoningEffort } from '../../types';
import { Modal } from '../ui';
import type { LLMModelOption } from '../../types/llmModel';

interface Props {
  config: LLMConfig | null;
  duplicateFrom?: LLMConfig | null;
  domains: Domain[];
  onClose: () => void;
  onSave: () => void;
}

export const LlmConfigModal: React.FC<Props> = ({ config, duplicateFrom, domains, onClose, onSave }) => {
  const [modelOptions, setModelOptions] = useState<LLMModelOption[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);

  useEffect(() => {
    loadModelOptions();
  }, []);

  const loadModelOptions = async () => {
    try {
      const options = await llmModelService.getModelOptions();
      setModelOptions(options);
    } catch (err) {
      console.error('Failed to load model options:', err);
      // Fallback to empty array - user can still type a model name
    } finally {
      setLoadingModels(false);
    }
  };
  const isEdit = !!config;
  const isDuplicate = !!duplicateFrom;
  const source = config || duplicateFrom;
  const [formData, setFormData] = useState({
    domain: source?.domain || (domains[0]?.name || ''),
    name: isDuplicate ? `${source?.name} (copie)` : (source?.name || ''),
    version: isDuplicate ? '' : (source?.version || ''),
    description: source?.description || '',
    model_name: source?.model_name || 'gpt-4o-mini',
    model_temperature: source?.model_temperature?.toString() || '0.00',
    model_max_tokens: source?.model_max_tokens?.toString() || '16000',
    model_top_p: source?.model_top_p?.toString() || '1.00',
    model_presence_penalty: source?.model_presence_penalty?.toString() || '0.00',
    model_frequency_penalty: source?.model_frequency_penalty?.toString() || '0.00',
    model_seed: source?.model_seed?.toString() || '42',
    // PROD1.9 — famille GPT-5 : '' = non applicable (modèle non-reasoning)
    model_reasoning_effort: source?.model_reasoning_effort || '',
    system_prompt: source?.system_prompt || "Tu es un assistant specialise dans l'extraction de donnees structurees a partir de documents. Reponds uniquement avec le JSON demande, sans texte supplementaire.",
    max_text_length: source?.max_text_length?.toString() || '60000',
    max_retries: source?.max_retries?.toString() || '1',
    // PROD1.7.7.1 — politique runtime extraction (par config LLM)
    extraction_timeout_s: source?.extraction_timeout_s?.toString() || '900',
    pipeline_max_retries: source?.pipeline_max_retries?.toString() || '2',
    pipeline_retry_delay_s: source?.pipeline_retry_delay_s?.toString() || '5',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const validateNumber = (value: string, min: number, max: number): boolean => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= min && num <= max;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Validation
    if (!validateNumber(formData.model_temperature, 0, 2)) {
      setError('La temperature doit etre entre 0.00 et 2.00');
      setSaving(false);
      return;
    }
    if (!validateNumber(formData.model_top_p, 0, 1)) {
      setError('Top P doit etre entre 0.00 et 1.00');
      setSaving(false);
      return;
    }
    if (!validateNumber(formData.model_presence_penalty, -2, 2)) {
      setError('Presence Penalty doit etre entre -2.00 et 2.00');
      setSaving(false);
      return;
    }
    if (!validateNumber(formData.model_frequency_penalty, -2, 2)) {
      setError('Frequency Penalty doit etre entre -2.00 et 2.00');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        description: formData.description || undefined,
        model_name: formData.model_name,
        model_temperature: parseFloat(formData.model_temperature),
        model_max_tokens: parseInt(formData.model_max_tokens, 10),
        model_top_p: parseFloat(formData.model_top_p),
        model_presence_penalty: parseFloat(formData.model_presence_penalty),
        model_frequency_penalty: parseFloat(formData.model_frequency_penalty),
        model_seed: formData.model_seed ? parseInt(formData.model_seed, 10) : undefined,
        // PROD1.9 — '' (modèle non-reasoning) → null en base
        model_reasoning_effort: formData.model_reasoning_effort
          ? (formData.model_reasoning_effort as ReasoningEffort)
          : null,
        system_prompt: formData.system_prompt,
        max_text_length: parseInt(formData.max_text_length, 10),
        max_retries: parseInt(formData.max_retries, 10),
        extraction_timeout_s: parseInt(formData.extraction_timeout_s, 10),
        pipeline_max_retries: parseInt(formData.pipeline_max_retries, 10),
        pipeline_retry_delay_s: parseInt(formData.pipeline_retry_delay_s, 10),
      };

      if (isEdit && config) {
        await llmConfigService.update(config.id, payload);
      } else {
        await llmConfigService.create({
          domain: formData.domain,
          version: formData.version,
          ...payload,
        } as CreateLLMConfigRequest);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Modifier la Configuration LLM' : (isDuplicate ? 'Dupliquer la Configuration LLM' : 'Nouvelle Configuration LLM')}
      size="lg"
    >
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Domaine <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2"
              required
            >
              {domains.map((d, index) => (
                <option key={`${d.name}-${index}`} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Le domaine ne peut pas etre modifie apres creation
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Version <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              disabled={isEdit}
              className="w-full border rounded px-3 py-2"
              placeholder="ex: 1.0.0"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              La version ne peut pas etre modifiee apres creation
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border rounded px-3 py-2"
            placeholder="ex: Config production"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full border rounded px-3 py-2"
            rows={2}
            placeholder="Description optionnelle de cette configuration"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Modele LLM</label>
          <select
            value={formData.model_name}
            onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
            className="w-full border rounded px-3 py-2"
            disabled={loadingModels}
          >
            {loadingModels ? (
              <option>Chargement...</option>
            ) : modelOptions.length > 0 ? (
              modelOptions.map((option) => (
                <option key={option.model_name} value={option.model_name}>
                  {option.display_name} ({option.provider})
                </option>
              ))
            ) : (
              <option value={formData.model_name}>{formData.model_name}</option>
            )}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Modele utilise pour les extractions LLM
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Temperature (0.00 - 2.00)
            </label>
            <input
              type="number"
              value={formData.model_temperature}
              onChange={(e) => setFormData({ ...formData, model_temperature: e.target.value })}
              min="0"
              max="2"
              step="0.01"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              0 = deterministe, 2 = tres creatif
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tokens maximum</label>
            <input
              type="number"
              value={formData.model_max_tokens}
              onChange={(e) => setFormData({ ...formData, model_max_tokens: e.target.value })}
              min="100"
              max="128000"
              step="100"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Limite de tokens pour la reponse
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Top P (0.00 - 1.00)
            </label>
            <input
              type="number"
              value={formData.model_top_p}
              onChange={(e) => setFormData({ ...formData, model_top_p: e.target.value })}
              min="0"
              max="1"
              step="0.01"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Nucleus sampling (1 = pas de filtrage)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Seed</label>
            <input
              type="number"
              value={formData.model_seed}
              onChange={(e) => setFormData({ ...formData, model_seed: e.target.value })}
              min="0"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Pour reproductibilite (optionnel)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reasoning Effort</label>
            <select
              value={formData.model_reasoning_effort}
              onChange={(e) => setFormData({ ...formData, model_reasoning_effort: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">— (non applicable)</option>
              <option value="minimal">minimal</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Famille GPT-5 / o-series uniquement. Ignoré pour gpt-4.1-mini, gpt-4o-mini, etc.
              Ces modèles utilisent max_completion_tokens et n'acceptent pas temperature/top_p.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Presence Penalty (-2.00 - 2.00)
            </label>
            <input
              type="number"
              value={formData.model_presence_penalty}
              onChange={(e) => setFormData({ ...formData, model_presence_penalty: e.target.value })}
              min="-2"
              max="2"
              step="0.01"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Penalise les tokens deja presents
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Frequency Penalty (-2.00 - 2.00)
            </label>
            <input
              type="number"
              value={formData.model_frequency_penalty}
              onChange={(e) => setFormData({ ...formData, model_frequency_penalty: e.target.value })}
              min="-2"
              max="2"
              step="0.01"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Penalise selon la frequence
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">System Prompt</label>
          <textarea
            value={formData.system_prompt}
            onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
            className="w-full border rounded px-3 py-2"
            rows={3}
            placeholder="Instructions pour le role du modele LLM"
          />
          <p className="text-xs text-gray-500 mt-1">
            Instructions envoyees au LLM pour definir son role et comportement
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Longueur max. texte (caracteres)</label>
            <input
              type="number"
              value={formData.max_text_length}
              onChange={(e) => setFormData({ ...formData, max_text_length: e.target.value })}
              min="1000"
              max="500000"
              step="1000"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Limite de caracteres du texte OCR envoye au LLM
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Retries max. (client LLM)</label>
            <input
              type="number"
              value={formData.max_retries}
              onChange={(e) => setFormData({ ...formData, max_retries: e.target.value })}
              min="0"
              max="10"
              step="1"
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Nombre de tentatives en cas d'echec API (retry interne du client LLM)
            </p>
          </div>
        </div>

        {/* PROD1.7.7.1 — Politique runtime extraction */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-semibold mb-3">Politique runtime extraction</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Timeout extraction (s)</label>
              <input
                type="number"
                value={formData.extraction_timeout_s}
                onChange={(e) => setFormData({ ...formData, extraction_timeout_s: e.target.value })}
                min="60"
                max="7200"
                step="60"
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Temps max (s) pour traiter un document. Default 900 (15 min).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Retries pipeline</label>
              <input
                type="number"
                value={formData.pipeline_max_retries}
                onChange={(e) => setFormData({ ...formData, pipeline_max_retries: e.target.value })}
                min="0"
                max="10"
                step="1"
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Retries du pipeline complet si une etape plante.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Delai entre retries (s)</label>
              <input
                type="number"
                value={formData.pipeline_retry_delay_s}
                onChange={(e) => setFormData({ ...formData, pipeline_retry_delay_s: e.target.value })}
                min="0"
                max="300"
                step="1"
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Delai entre retries pipeline.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
